import React, { useState, useEffect } from 'react';
import { apiClient } from '../config/api';

interface TherapeuticTarget {
  id: number;
  client_id: string;
  nutrient_key: string;
  target_value: number;
  target_min?: number;
  target_max?: number;
  unit: string;
  condition_context?: string;
  clinical_rationale?: string;
  expected_duration_days?: number;
  monitoring_frequency_days: number;
  priority_level: 'low' | 'medium' | 'high' | 'critical';
  is_active: boolean;
  client_explanation?: string;
  food_recommendations?: string;
  effective_from: string;
  effective_until?: string;
  created_at: string;
  updated_at: string;
}

interface TherapeuticTargetsPageProps {
  user?: any;
  coachMode?: boolean;
  clientId?: number;
}

export const TherapeuticTargetsPage: React.FC<TherapeuticTargetsPageProps> = ({ 
  user, 
  coachMode = false, 
  clientId 
}) => {
  const [targets, setTargets] = useState<TherapeuticTarget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTarget, setEditingTarget] = useState<TherapeuticTarget | null>(null);

  const [targetForm, setTargetForm] = useState({
    nutrient_key: '',
    target_value: '',
    target_min: '',
    target_max: '',
    unit: '',
    condition_context: '',
    clinical_rationale: '',
    expected_duration_days: '',
    monitoring_frequency_days: '7',
    priority_level: 'medium',
    client_explanation: '',
    food_recommendations: '',
    effective_until: ''
  });

  // Common nutrient options with their typical units
  const nutrientOptions = [
    { key: 'vitamin_d', label: 'Vitamin D', unit: 'IU' },
    { key: 'vitamin_b12', label: 'Vitamin B12', unit: 'μg' },
    { key: 'iron', label: 'Iron', unit: 'mg' },
    { key: 'calcium', label: 'Calcium', unit: 'mg' },
    { key: 'magnesium', label: 'Magnesium', unit: 'mg' },
    { key: 'zinc', label: 'Zinc', unit: 'mg' },
    { key: 'folate', label: 'Folate', unit: 'μg' },
    { key: 'omega_3', label: 'Omega-3 EPA/DHA', unit: 'mg' },
    { key: 'protein', label: 'Protein', unit: 'g' },
    { key: 'fiber', label: 'Fiber', unit: 'g' }
  ];

  useEffect(() => {
    if (clientId) {
      loadTherapeuticTargets();
    }
  }, [clientId]);

  const loadTherapeuticTargets = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!clientId) return;
      
      // Use API client's request method - cast to any to access private method
      const data = await (apiClient as any).request(`/api/coach/client/${clientId}/therapeutic-targets`) as { targets: TherapeuticTarget[] };
      setTargets(data.targets || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load therapeutic targets');
    } finally {
      setLoading(false);
    }
  };

  const handleNutrientChange = (nutrientKey: string) => {
    const nutrient = nutrientOptions.find(n => n.key === nutrientKey);
    setTargetForm(prev => ({
      ...prev,
      nutrient_key: nutrientKey,
      unit: nutrient?.unit || ''
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...targetForm,
        target_value: parseFloat(targetForm.target_value),
        target_min: targetForm.target_min ? parseFloat(targetForm.target_min) : null,
        target_max: targetForm.target_max ? parseFloat(targetForm.target_max) : null,
        expected_duration_days: targetForm.expected_duration_days ? parseInt(targetForm.expected_duration_days) : null,
        monitoring_frequency_days: parseInt(targetForm.monitoring_frequency_days),
        effective_until: targetForm.effective_until || null
      };

      const endpoint = editingTarget 
        ? `/api/coach/client/${clientId}/therapeutic-targets/${editingTarget.id}`
        : `/api/coach/client/${clientId}/therapeutic-targets`;

      await (apiClient as any).request(endpoint, {
        method: editingTarget ? 'PUT' : 'POST',
        body: JSON.stringify(payload)
      });

      // Reset form and reload targets
      setTargetForm({
        nutrient_key: '',
        target_value: '',
        target_min: '',
        target_max: '',
        unit: '',
        condition_context: '',
        clinical_rationale: '',
        expected_duration_days: '',
        monitoring_frequency_days: '7',
        priority_level: 'medium',
        client_explanation: '',
        food_recommendations: '',
        effective_until: ''
      });
      setShowAddForm(false);
      setEditingTarget(null);
      await loadTherapeuticTargets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save therapeutic target');
    }
  };

  const handleEdit = (target: TherapeuticTarget) => {
    setEditingTarget(target);
    setTargetForm({
      nutrient_key: target.nutrient_key,
      target_value: target.target_value.toString(),
      target_min: target.target_min?.toString() || '',
      target_max: target.target_max?.toString() || '',
      unit: target.unit,
      condition_context: target.condition_context || '',
      clinical_rationale: target.clinical_rationale || '',
      expected_duration_days: target.expected_duration_days?.toString() || '',
      monitoring_frequency_days: target.monitoring_frequency_days.toString(),
      priority_level: target.priority_level,
      client_explanation: target.client_explanation || '',
      food_recommendations: target.food_recommendations || '',
      effective_until: target.effective_until || ''
    });
    setShowAddForm(true);
  };

  const handleDeactivate = async (targetId: number) => {
    try {
      await (apiClient as any).request(`/api/coach/client/${clientId}/therapeutic-targets/${targetId}`, {
        method: 'DELETE'
      });

      await loadTherapeuticTargets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate target');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!coachMode) {
    return (
      <div className="p-6 text-center">
        <div className="text-6xl mb-4">🔒</div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Coach Access Required</h2>
        <p className="text-gray-600">
          Therapeutic targets can only be managed by qualified nutrition coaches.
        </p>
      </div>
    );
  }

  if (!clientId) {
    return (
      <div className="p-6 text-center">
        <div className="text-6xl mb-4">👤</div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Select a Client</h2>
        <p className="text-gray-600">
          Please select a client to manage their therapeutic nutrition targets.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Therapeutic Nutrition Targets</h1>
        <p className="text-gray-600">
          Set personalized therapeutic nutrition targets for {user?.full_name || 'this client'} based on clinical assessment and treatment goals.
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="mb-6">
        <button
          onClick={() => {
            setShowAddForm(!showAddForm);
            setEditingTarget(null);
            if (!showAddForm) {
              setTargetForm({
                nutrient_key: '',
                target_value: '',
                target_min: '',
                target_max: '',
                unit: '',
                condition_context: '',
                clinical_rationale: '',
                expected_duration_days: '',
                monitoring_frequency_days: '7',
                priority_level: 'medium',
                client_explanation: '',
                food_recommendations: '',
                effective_until: ''
              });
            }
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          {showAddForm ? 'Cancel' : '+ Add New Target'}
        </button>
      </div>

      {showAddForm && (
        <div className="mb-8 bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingTarget ? 'Edit Therapeutic Target' : 'Add New Therapeutic Target'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nutrient *
                </label>
                <select
                  value={targetForm.nutrient_key}
                  onChange={(e) => handleNutrientChange(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select nutrient...</option>
                  {nutrientOptions.map(nutrient => (
                    <option key={nutrient.key} value={nutrient.key}>
                      {nutrient.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Value *
                </label>
                <div className="flex">
                  <input
                    type="number"
                    value={targetForm.target_value}
                    onChange={(e) => setTargetForm(prev => ({...prev, target_value: e.target.value}))}
                    step="0.001"
                    required
                    className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    value={targetForm.unit}
                    onChange={(e) => setTargetForm(prev => ({...prev, unit: e.target.value}))}
                    placeholder="Unit"
                    required
                    className="w-20 border border-l-0 border-gray-300 rounded-r-md px-2 py-2 focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Range Min
                </label>
                <input
                  type="number"
                  value={targetForm.target_min}
                  onChange={(e) => setTargetForm(prev => ({...prev, target_min: e.target.value}))}
                  step="0.001"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Range Max
                </label>
                <input
                  type="number"
                  value={targetForm.target_max}
                  onChange={(e) => setTargetForm(prev => ({...prev, target_max: e.target.value}))}
                  step="0.001"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority Level
                </label>
                <select
                  value={targetForm.priority_level}
                  onChange={(e) => setTargetForm(prev => ({...prev, priority_level: e.target.value}))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monitoring Frequency (days)
                </label>
                <input
                  type="number"
                  value={targetForm.monitoring_frequency_days}
                  onChange={(e) => setTargetForm(prev => ({...prev, monitoring_frequency_days: e.target.value}))}
                  min="1"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Condition Context
              </label>
              <input
                type="text"
                value={targetForm.condition_context}
                onChange={(e) => setTargetForm(prev => ({...prev, condition_context: e.target.value}))}
                placeholder="e.g. Iron deficiency anemia, B12 deficiency"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Clinical Rationale
              </label>
              <textarea
                value={targetForm.clinical_rationale}
                onChange={(e) => setTargetForm(prev => ({...prev, clinical_rationale: e.target.value}))}
                placeholder="Clinical reasoning for this therapeutic target..."
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Explanation
              </label>
              <textarea
                value={targetForm.client_explanation}
                onChange={(e) => setTargetForm(prev => ({...prev, client_explanation: e.target.value}))}
                placeholder="Simple explanation for the client about why this target is important..."
                rows={2}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Food Recommendations
              </label>
              <textarea
                value={targetForm.food_recommendations}
                onChange={(e) => setTargetForm(prev => ({...prev, food_recommendations: e.target.value}))}
                placeholder="Specific foods to help achieve this target..."
                rows={2}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingTarget(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {editingTarget ? 'Update Target' : 'Create Target'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Targets List */}
      <div className="space-y-4">
        {targets.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎯</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Therapeutic Targets Set</h3>
            <p className="text-gray-600 mb-4">
              Create personalized nutrition targets based on this client's therapeutic needs.
            </p>
          </div>
        ) : (
          targets.map((target) => (
            <div key={target.id} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {nutrientOptions.find(n => n.key === target.nutrient_key)?.label || target.nutrient_key}
                    </h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(target.priority_level)}`}>
                      {target.priority_level.charAt(0).toUpperCase() + target.priority_level.slice(1)} Priority
                    </span>
                    {!target.is_active && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                        Inactive
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-600">Target Value</p>
                      <p className="font-medium">
                        {target.target_value} {target.unit}
                        {target.target_min && target.target_max && (
                          <span className="text-gray-500 text-sm ml-2">
                            (Range: {target.target_min}-{target.target_max} {target.unit})
                          </span>
                        )}
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600">Monitor Every</p>
                      <p className="font-medium">{target.monitoring_frequency_days} days</p>
                    </div>
                  </div>

                  {target.condition_context && (
                    <div className="mb-3">
                      <p className="text-sm text-gray-600">Condition</p>
                      <p className="text-sm">{target.condition_context}</p>
                    </div>
                  )}

                  {target.client_explanation && (
                    <div className="mb-3">
                      <p className="text-sm text-gray-600">For Client</p>
                      <p className="text-sm">{target.client_explanation}</p>
                    </div>
                  )}

                  {target.clinical_rationale && (
                    <div className="mb-3">
                      <p className="text-sm text-gray-600">Clinical Rationale</p>
                      <p className="text-sm">{target.clinical_rationale}</p>
                    </div>
                  )}

                  {target.food_recommendations && (
                    <div className="mb-3">
                      <p className="text-sm text-gray-600">Food Recommendations</p>
                      <p className="text-sm">{target.food_recommendations}</p>
                    </div>
                  )}

                  <div className="text-xs text-gray-500">
                    Created: {new Date(target.created_at).toLocaleDateString()}
                    {target.effective_until && (
                      <span className="ml-3">
                        Until: {new Date(target.effective_until).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex space-x-2 ml-4">
                  <button
                    onClick={() => handleEdit(target)}
                    className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 border border-blue-200 rounded"
                  >
                    Edit
                  </button>
                  {target.is_active && (
                    <button
                      onClick={() => handleDeactivate(target.id)}
                      className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 border border-red-200 rounded"
                    >
                      Deactivate
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};